import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ExternalLink, Github, X } from 'lucide-react';
import axios from 'axios';
import { projectsData } from '../utils/mockData';
import ProjectVisual from './ProjectVisual';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Projects = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [selectedProject, setSelectedProject] = useState(null);
  const dialogRef = useRef(null);
  const scrollPositionRef = useRef({ x: 0, y: 0 });
  const triggerRef = useRef(null);
  const restoreScrollRef = useRef(false);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return undefined;

    if (selectedProject) {
      if (!dialog.open) dialog.showModal();
      dialog.scrollTop = 0;
      return undefined;
    }

    if (dialog.open) dialog.close();
    if (restoreScrollRef.current) {
      const { x, y } = scrollPositionRef.current;
      restoreScrollRef.current = false;
      window.requestAnimationFrame(() => {
        window.scrollTo(x, y);
        triggerRef.current?.focus({ preventScroll: true });
      });
    }
    return undefined;
  }, [selectedProject]);

  const openProject = (project, event) => {
    scrollPositionRef.current = { x: window.scrollX, y: window.scrollY };
    triggerRef.current = event.currentTarget;
    restoreScrollRef.current = true;
    setSelectedProject(project);
  };

  const closeProject = () => setSelectedProject(null);

  const [filter, setFilter] = useState('All');
  const [projects, setProjects] = useState(projectsData);

  useEffect(() => {
    axios
      .get(`${API}/content/projects`)
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length) setProjects(res.data);
      })
      .catch(() => {});
  }, []);

  // Get unique tags
  const allTags = ['All', ...new Set(projects.flatMap((p) => p.tags))];

  // Filter projects
  const filteredProjects =
    filter === 'All'
      ? projects
      : projects.filter((p) => p.tags.includes(filter));

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const projectVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.645, 0.045, 0.355, 1],
      },
    },
  };

  return (
    <section className="projects-section" id="projects">
      <div className="section-container">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
          className="section-header"
        >
          <h2 className="section-title">Selected work</h2>
          <p className="section-subtitle">
            A collection of cloud-native engineering themes and hands-on platform work
          </p>
        </motion.div>

        {/* Filter Tags */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="project-filters"
        >
          {allTags.map((tag) => (
            <motion.button
              key={tag}
              className={`filter-tag ${filter === tag ? 'active' : ''}`}
              onClick={() => setFilter(tag)}
              aria-pressed={filter === tag}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {tag}
            </motion.button>
          ))}
        </motion.div>

        {/* Projects Grid */}
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          className="projects-grid"
        >
          <AnimatePresence mode="wait">
            {filteredProjects.map((project) => (
              <motion.div
                key={project.id}
                variants={projectVariants}
                layout
                className="project-card"
                whileHover={{
                  y: -10,
                  transition: { duration: 0.3, ease: 'easeOut' },
                }}
              >
                <ProjectVisual project={project} />
                <div className="project-header">
                  <span className="project-year">{project.year}</span>
                  <div className="project-tags">
                    {project.tags.slice(0, 2).map((tag, idx) => (
                      <span key={idx} className="project-tag">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <h3 className="project-title">{project.title}</h3>
                <p className="project-summary">{project.summary}</p>
                <div className="project-footer">
                  <button type="button" className="project-link-text project-details-button" data-pointer-label="Open" aria-label={`View details: ${project.title}`} onClick={(event) => openProject(project, event)}>View details</button>
                  <ExternalLink size={16} />
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Native dialog keeps keyboard focus inside and restores it on close. */}
      <dialog ref={dialogRef} className="project-modal portfolio-project-dialog" aria-labelledby="project-dialog-title" data-lenis-prevent
        onCancel={closeProject} onClose={closeProject}
        onClick={(event) => { if (event.target === event.currentTarget) { const rect = event.currentTarget.getBoundingClientRect(); if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) closeProject(); } }}>
        {selectedProject && <>
              <button
                type="button"
                className="modal-close"
                aria-label="Close project details"
                onClick={closeProject}
              >
                <X size={24} />
              </button>

              <div className="modal-content">
                <ProjectVisual project={selectedProject} modal />
                <div className="modal-header">
                  <span className="modal-year">{selectedProject.year}</span>
                  <div className="modal-tags">
                    {selectedProject.tags.map((tag, idx) => (
                      <span key={idx} className="modal-tag">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                <h2 id="project-dialog-title" className="modal-title">{selectedProject.title}</h2>
                <p className="modal-description">{selectedProject.description}</p>

                <div className="modal-section">
                  <h3 className="modal-section-title">Areas of focus</h3>
                  <ul className="modal-list">
                    {selectedProject.focus.map((item, idx) => (
                      <li key={idx} className="modal-list-item">
                        <span className="list-bullet"></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="modal-section">
                  <h3 className="modal-section-title">Outcome</h3>
                  <p className="modal-outcome">{selectedProject.outcome}</p>
                </div>

                <a
                  href={selectedProject.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="modal-github-link"
                >
                  <Github size={18} />
                  View on GitHub
                </a>
              </div>
        </>}
      </dialog>
    </section>
  );
};

export default Projects;
